import { queryClient } from 'App';
import debounce from 'lodash/debounce';

import { clinicalNotesQueryKeys } from 'lib/constants/queries/queryKeys';
import { isLocalAddendumLifecycleEcho } from 'lib/helpers/addendumLifecycleEcho';

// A draft addendum was started, signed or discarded somewhere else (another tab, another device, the
// biller alongside the provider). The event carries no patient/note/addendum id on purpose — the room is
// every practice member, so the emitter sends nothing identifying (see its counterpart on the backend) —
// which is also why the invalidation is unscoped. Broad prefixes would be needed regardless: this
// context keys patient id as a number while the Superbill keys it as the URL string, so an id-scoped key
// matches nothing there. Refetching is all this does, and each refetch is authorized server-side —
// AddendumModeContextProvider's stale guard decides what the refreshed answer means for a session that
// still holds the draft open.
const refreshAddendumQueries = () => {
  queryClient.invalidateQueries({ queryKey: ['clinicalNoteAddendum'] });
  queryClient.invalidateQueries({ queryKey: ['clinicalNoteAddendums'] });
  clinicalNotesQueryKeys.forEach((key) => {
    queryClient.invalidateQueries({ queryKey: [key] });
  });

  // The invalidations above refetch only what is MOUNTED here. Every addendum-list and note-list reader
  // sets refetchOnMount:false (the signed-forms list also staleTime:Infinity), so an unmounted copy would
  // serve its pre-event data on the next mount — the addendum signed elsewhere never appearing in this
  // session's note or preview. Dropping the inactive copies makes that mount fetch fresh.
  queryClient.removeQueries({ queryKey: ['clinicalNoteAddendums'], type: 'inactive' });
  queryClient.removeQueries({ queryKey: ['clinicalNoteRead'], type: 'inactive' });
  clinicalNotesQueryKeys.forEach((key) => {
    queryClient.removeQueries({ queryKey: [key], type: 'inactive' });
  });
};

// The event reaches every practice member for every draft / sign / discard in the practice, and each
// invalidation restarts the note-list fetches without aborting the ones in flight — a busy biller's
// burst used to queue a request pair per event in every open chart. The first event refreshes at once,
// the rest of a burst collapses into one trailing refresh, and maxWait bounds a steady stream.
const REFRESH_WAIT_MS = 1000;
const REFRESH_MAX_WAIT_MS = 5000;

// A hidden tab has nobody reading its note lists, yet each refresh there still fires the full-history
// and practice-queue fetches — one set per open chart tab, per event. Hold the refresh until the tab
// is shown again; any number of events while hidden collapse into that one catch-up refresh.
let hasPendingHiddenRefresh = false;

const refreshWhenVisible = () => {
  if (document.visibilityState === 'hidden') {
    hasPendingHiddenRefresh = true;
    return;
  }
  refreshAddendumQueries();
};

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' || !hasPendingHiddenRefresh) return;
  hasPendingHiddenRefresh = false;
  // A trailing call still queued from the hidden burst would repeat this refresh moments later.
  scheduleRefresh.cancel();
  refreshAddendumQueries();
});

const scheduleRefresh = debounce(refreshWhenVisible, REFRESH_WAIT_MS, {
  leading: true,
  trailing: true,
  maxWait: REFRESH_MAX_WAIT_MS
});

export default () => {
  // "Somewhere else" excluded: the room includes the session that caused the event.
  if (isLocalAddendumLifecycleEcho()) return;

  scheduleRefresh();
};
